import React, { FC, useMemo, useCallback, SetStateAction } from 'react';
import { LoadingScreen } from '@/components/feedback/loading-screen';
import { useFetchData } from '@/hooks';
import {
  Chip,
  Stack,
  Avatar,
  TextField,
  Typography,
  Autocomplete,
  AutocompleteProps,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Iconify from '@/components/media/iconify';
import {
  getOrderStatusColor,
  getContactStatusColor,
  getOrganizationStatusColor,
  getSubscriptionStatusColor,
} from '@/utils/formatters/status-colors';

type Props = {
  name: string;
  filters: Record<string, any>;
  url?: string;
  onChange: (value: SetStateAction<any>) => void;
  label?: string;
  multiple?: boolean;
  options?: Record<string, any>[];
  showLogo?: boolean;
  showCountryFlag?: boolean;
  disableClearable?: boolean;
  isPost?: boolean;
  postData?: Record<string, any>;
  params?: Record<string, any>;
  optionsMapper?: (data: any) => Record<string, any>[];
} & Omit<
  AutocompleteProps<any, boolean, boolean, boolean>,
  'name' | 'value' | 'onChange' | 'options' | 'renderInput'
>;

// Helper function to get status color based on context
const getStatusColorForField = (fieldName: string, statusId: string) => {
  if (fieldName !== 'status') return undefined;

  // Try to determine context from URL or other means
  const currentPath = window.location.pathname;

  if (currentPath.includes('/orders')) {
    return getOrderStatusColor(statusId);
  }
  if (currentPath.includes('/contacts')) {
    return getContactStatusColor(statusId);
  }
  if (currentPath.includes('/organizations')) {
    return getOrganizationStatusColor(statusId);
  }
  if (currentPath.includes('/subscriptions')) {
    return getSubscriptionStatusColor(statusId);
  }

  // Default fallback
  return undefined;
};

export const AutocompleteSelect: FC<Props> = ({
  label,
  name,
  url,
  filters,
  onChange,
  optionsMapper,
  multiple = true,
  disableClearable = false,
  options = [],
  showLogo = false,
  showCountryFlag = false,
  isPost = false,
  postData = null,
  params = null,
  ...extraProps
}) => {
  const { t } = useTranslation();

  const defaultLabel = useMemo(() => label || t('Autocomplete'), [label, t]);

  const defaultOptionsMapper = useMemo(
    () =>
      optionsMapper ||
      ((mapOptions: Record<string, any>[]) =>
        mapOptions.map((option: Record<string, any>) => ({
          label: option?.name ?? option?.label ?? option?.value ?? option?._id ?? '',
          showLogo: (!!option?.logo || !!option?.avatar) ?? false,
          ...option,
        }))),
    [optionsMapper]
  );

  const handleChange = useCallback(
    (
      event: React.SyntheticEvent,
      value: unknown,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<unknown> | undefined
    ) => {
      onChange((prevState: Record<string, any>) => ({
        ...prevState,
        page: 0,
        [name]: value,
      }));
    },
    [onChange, name]
  );

  const payloadFilter = useMemo(() => (isPost ? postData : null), [isPost, postData]);
  const filter = useMemo(
    () => (payloadFilter ? JSON.stringify(payloadFilter) : ''),
    [payloadFilter]
  );

  const { data, isLoading } = useFetchData(
    [defaultLabel, 'filter', filter, url || ''],
    url || '',
    { params },
    isPost,
    payloadFilter
  );

  const processedOptions = useMemo(() => {
    if (options && options.length > 0) {
      return options;
    }

    const finalData = (data && 'data' in data ? data.data : data) as Record<string, any>[];
    if (!finalData || typeof finalData !== 'object' || !Array.isArray(finalData)) {
      return [];
    }

    const result = defaultOptionsMapper(finalData);
    return result;
  }, [options, defaultOptionsMapper, data]);

  const renderOption = useCallback(
    (props: any, option: any) => {
      const { label: optionLabel, value, flag_image } = option;
      let optionId = option.id ?? option._id;
      if (!optionId) {
        optionId = value instanceof Object ? value._id : value;
      }
      return (
        <li {...props} key={optionId}>
          {showLogo && (
            <Avatar
              key={`avatar-${optionId}`}
              alt={optionLabel}
              src={decodeURI(option.avatar ? option.avatar.url : option.showLogo?.url)}
              sx={{ mr: 2, width: 24, height: 24 }}
              imgProps={{
                loading: 'lazy',
                sx: {
                  transform: 'scale(1.2)',
                  objectFit: 'cover',
                },
              }}
            />
          )}
          {showCountryFlag && flag_image && (
            <Iconify
              key={`flag-${optionId}`}
              icon={`flagpack:${flag_image.toLowerCase()}`}
              width={25}
              sx={{ mr: 1 }}
            />
          )}

          {optionLabel}
        </li>
      );
    },
    [showLogo, showCountryFlag]
  );

  const renderTags = useMemo(
    () => (selected: any[], getTagProps: any) =>
      selected.map((option, index) => {
        const { flag_image } = option;
        const statusColor = getStatusColorForField(name, option._id ?? option.id);

        return (
          <Chip
            {...getTagProps({ index })}
            key={`chip-${option._id}`}
            label={option.label}
            size="small"
            variant="soft"
            color={statusColor}
            icon={
              showCountryFlag && flag_image ? (
                <Iconify icon={`flagpack:${flag_image.toLowerCase()}`} width={16} />
              ) : undefined
            }
          />
        );
      }),
    [name, showCountryFlag]
  );

  return (
    <Stack>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {defaultLabel}
      </Typography>

      {isLoading ? (
        <LoadingScreen />
      ) : (
        <Autocomplete
          multiple={multiple}
          disableClearable={disableClearable}
          disableCloseOnSelect
          clearText={t('Clear')}
          closeText={t('Close')}
          limitTags={2}
          options={processedOptions}
          isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
          value={filters[name]}
          filterSelectedOptions
          onChange={handleChange}
          getOptionDisabled={(option: any) => (option.status ? option.status !== 'active' : false)}
          renderOption={renderOption}
          renderInput={(parameters: any) => (
            <TextField placeholder={defaultLabel} {...parameters} />
          )}
          renderTags={renderTags}
          {...extraProps}
        />
      )}
    </Stack>
  );
};
